import { Dialog } from "@ncino/pax-ui";
import { gradients } from "@/theme";
import { useEffect, useState } from "react";
import { TermsStep, type Terms } from "@/shared/components/AgreementGate/TermsStep";
import { OrderFormStep } from "@/shared/components/AgreementGate/OrderFormStep";
import { WorkflowStatus } from "@/services/ironclad/useIroncladWorkflow";

interface AgreementGateProps {
  terms: Terms;
  orderFormStatus: WorkflowStatus;
  onAccept: (version: number) => Promise<unknown>;
  onComplete: () => void;
  onSignNow: () => void;
  isAccepting?: boolean;
  error?: boolean;
}

export function AgreementGate({
  terms,
  orderFormStatus,
  onAccept,
  onComplete,
  onSignNow,
  isAccepting,
  error,
}: AgreementGateProps) {
  const [accepted, setAccepted] = useState(false);

  const handleAccept = async (version: number) => {
    try {
      await onAccept(version);
      setAccepted(true);
    } catch {
      // Failure is surfaced to the user via the `error` prop; stay on the terms step.
    }
  };

  const waitingOnStatus = accepted && orderFormStatus === "loading";
  const showOrderForm = accepted && orderFormStatus !== "loading" && orderFormStatus !== "signed";

  useEffect(() => {
    if (accepted && orderFormStatus === "signed") onComplete();
  }, [accepted, orderFormStatus, onComplete]);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={() => undefined}
      aria-labelledby="agreement-gate-title"
      data-testid="agreement-gate"
      slotProps={{
        backdrop: {
          sx: {
            opacity: 1,
            backgroundColor: "background.paper",
            backgroundImage: gradients.gatorLinear,
            backgroundRepeat: "no-repeat",
            backgroundSize: "100% 352px",
            backgroundPosition: "top",
          },
        },
        paper: {
          sx: { borderRadius: 2 },
        },
      }}
    >
      {showOrderForm ? (
        <OrderFormStep onSignNow={onSignNow} onEnterWithoutSigning={onComplete} />
      ) : (
        <TermsStep
          terms={terms}
          onAccept={handleAccept}
          isAccepting={isAccepting || waitingOnStatus}
          error={error}
        />
      )}
    </Dialog>
  );
}
